import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, FormControl, Validators } from '@angular/forms';
import { AuthenticationService } from '@servicios/authentication.service';
import { MessageService } from 'primeng/api';
import { ILogin } from '../loginDialog/loginDialog.component';
import { ConfigService } from '@servicios/config.service';
import { BreakpointService } from '@servicios/breakpoint.service';
import { passwordCoinciden } from 'app/_sharedFuctions/shared-functions';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css'],
    standalone: false
})

export class LoginComponent implements OnInit, OnDestroy {

  constructor(
    private AuthService: AuthenticationService,
    private messageService: MessageService,
    private fb: UntypedFormBuilder,
    public cs: ConfigService,
    public bs: BreakpointService,
    private router: Router
  ) {}

  isLoading = false
  hide = true
  appSettings= this.cs.appSettings
  text = 'bienvenida/o';
  isClientMode = false;
  emailsActive = false;
  loginErrorMessage = '';
  private subscriptions = new Subscription();
  emails = [
    {
      email: 'soporte@biwork.com.ar',
      id: 'soporte'
    },
    {
      email: 'administracion@biwork.com.ar',
      id: 'admin'
    }
  ];

  form = this.fb.group({
    user: ['', Validators.required],
    password: ['', Validators.required],
    website: ['']
  })

  forgot = false;
  adminForcedChange = false;
  resendCode = false;
  disabledButton = false;
  recoveryUserId = '';
  errorMessage = '';
  codeRequested = false;

  formRecovery = this.fb.group({
    userName: ['', Validators.required],
    code: ['', Validators.required],
    password: ['', [Validators.required, Validators.minLength(6)]],
    passwordVal: ['', [Validators.required, Validators.minLength(6)]]
  })

  ngOnInit(): void {
    this.isClientMode = this.router.url.includes('loginClient');
    this.emailsActive = this.cs.appSettings.frontend?.contactLoginClient?.active || false;
    
    if(this.emailsActive && this.cs.appSettings.frontend?.contactLoginClient?.emails?.length) {
      this.emails = this.cs.appSettings.frontend.contactLoginClient.emails;
    }

    this.formRecovery.setValidators(passwordCoinciden('password', 'passwordVal', this));
    this.formRecovery.updateValueAndValidity({ emitEvent: false });

    const authSub = this.AuthService.authorized$.subscribe({
      next: () => this.isLoading = false,
      error: err => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Ocurrio un error al acceder al sistema' });        
      }
    });
    this.subscriptions.add(authSub);

    const formSub = this.form.valueChanges.subscribe(() => {
      if (this.loginErrorMessage) {
        this.loginErrorMessage = '';
      }
    });
    this.subscriptions.add(formSub);
  }

  userLogin(form: ILogin) {
    if (this.isLoading) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.loginErrorMessage = '';
    
    if (this.form.value.website) {
       setTimeout(() => {
         this.isLoading = false;
         this.loginErrorMessage = 'Credenciales inválidas';
         this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Credenciales inválidas' }); 
       }, 500);
       return;
    }

    const loginPromise = this.isClientMode 
      ? this.AuthService.clientLogin(form.user, form.password)
      : this.AuthService.userLogin(form.user, form.password);

    loginPromise
      .then(() => {
        this.isLoading = false;
      })
      .catch(err => {
        this.isLoading = false;
        const msg = err?.error?.message || err?.graphQLErrors?.[0]?.message || err?.message || 'Usuario o contraseña incorrectos';
        this.loginErrorMessage = msg;
        if (err?.message?.includes('Debe cambiar su contrase')) {
          this.adminForcedChange = true;
          this.toForgot();
          this.formRecovery.patchValue({ userName: form.user });
        }
      });
  }

  copyTo(id: string) {
    const emailToCopy = this.emails.find(email => email.id === id)?.email || id;
    navigator.clipboard.writeText(emailToCopy)
    this.messageService.add({ severity: 'success', summary: 'Copiado', detail: 'Email copiado al portapapeles' });
  }

  toRegister(){
    this.router.navigateByUrl('/loginClient/register') 
  }

  toForgot() {
    this.forgot = !this.forgot;
    if (!this.forgot) {
      this.adminForcedChange = false;
    }
    this.disabledButton = false;
    this.errorMessage = '';
    this.loginErrorMessage = '';
    this.codeRequested = false;
    this.form.reset();
    this.formRecovery.reset();
  }

  recoverPassword() {
    this.isLoading = true;
    this.errorMessage = '';
    
    const requestObservable = this.isClientMode
      ? this.AuthService.recoverPassword(this.formRecovery.value.userName.toString())
      : this.AuthService.recoverInternalPassword(this.formRecovery.value.userName);

    requestObservable.subscribe({
        next: ({ data }) => {
          // data?.sendInternalUserRecoveryMail or data?.sendRecoveryPasswordMail
          this.messageService.add({ severity: 'success', summary: 'Código enviado', detail: 'Revisa tu correo electrónico' });
          this.codeRequested = true;
          this.counterCode();
          this.isLoading = false;
        },
        error: err => {
          this.errorMessage = err.message;
          this.isLoading = false;
        }
      });
  }

  counterCode() {    
    this.disabledButton = true;
    setTimeout(() => {
      this.disabledButton = false;      
    }, 60000); // 60 segundos de cooldown
  }

  resetPassword() {
    this.isLoading = true;
    this.errorMessage = '';
    const { userName, code, password } = this.formRecovery.value;

    const requestObservable = this.isClientMode
      ? this.AuthService.ResetUser(userName, password, code)
      : this.AuthService.resetInternalUser(userName, password, code);

    requestObservable.subscribe({
        next: ({ data }) => {
          // data?.resetInternalUserPassword or data?.resetUserPassword
          this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Contraseña actualizada con exito' });
          this.toForgot(); // volver al login
          this.isLoading = false;
        },
        error: err => {
          this.errorMessage = err.message;
          this.isLoading = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

}